/**
 * ScenarioList Component
 * Card-based view of evaluation scenarios with score/status display
 * Enhanced with expandable details and inline tag editing
 */

import React, { useState } from 'react';
import { Play, Edit2, Trash2, MessageSquare, History, Copy, ChevronDown, ChevronUp, X, Target, Bot } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { EvalScenario } from '@/types/evalScenarios';

interface ScenarioListProps {
  scenarios: EvalScenario[];
  onEdit: (scenario: EvalScenario) => void;
  onDelete: (scenario: EvalScenario) => void;
  onRun: (scenario: EvalScenario) => void;
  onDuplicate?: (scenario: EvalScenario) => void;
  onViewHistory?: (scenario: EvalScenario) => void;
  onTagsChange?: (scenarioId: string, tags: string[]) => void;
  runningScenarioIds?: Set<string>;
  // Selection support for test suites
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
}

export const ScenarioList: React.FC<ScenarioListProps> = ({
  scenarios,
  onEdit,
  onDelete,
  onRun,
  onDuplicate,
  onViewHistory,
  onTagsChange,
  runningScenarioIds = new Set(),
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
}) => {
  const { messages, language } = useI18n();
  const t = messages.evalScenarios;

  // Track expanded scenarios
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Track new tag input per scenario
  const [newTagInputs, setNewTagInputs] = useState<Record<string, string>>({});

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Format score as percentage
  const formatScore = (score: number | undefined | null): string => {
    if (score === null || score === undefined) return '-';
    return (score * 100).toFixed(0) + '%';
  };

  // Format date
  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status class for badge
  const getStatusClass = (scenario: EvalScenario): string => {
    if (!scenario.latest_result) return 'bg-gray-100 text-gray-800';
    return scenario.latest_result.passed
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  // Get status text
  const getStatusText = (scenario: EvalScenario): string => {
    if (!scenario.latest_result) return t.neverRun;
    return scenario.latest_result.passed ? t.passed : t.failed;
  };

  // Handle tag removal
  const handleRemoveTag = (scenario: EvalScenario, tagToRemove: string) => {
    if (!onTagsChange) return;
    const newTags = (scenario.tags || []).filter(tag => tag !== tagToRemove);
    onTagsChange(scenario.id, newTags);
  };

  // Handle tag addition
  const handleAddTag = (scenario: EvalScenario, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const input = e.currentTarget;
    const newTag = input.value.trim();
    if (!newTag || !onTagsChange) return;

    const currentTags = scenario.tags || [];
    if (!currentTags.includes(newTag)) {
      onTagsChange(scenario.id, [...currentTags, newTag]);
    }
    setNewTagInputs(prev => ({ ...prev, [scenario.id]: '' }));
  };

  // Toggle scenario selection
  const toggleSelection = (scenarioId: string) => {
    if (!onSelectionChange) return;
    const newSelection = new Set(selectedIds);
    if (newSelection.has(scenarioId)) {
      newSelection.delete(scenarioId);
    } else {
      newSelection.add(scenarioId);
    }
    onSelectionChange(newSelection);
  };

  // Toggle all scenarios selection
  const toggleSelectAll = () => {
    if (!onSelectionChange) return;
    if (selectedIds.size === scenarios.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(scenarios.map(s => s.id)));
    }
  };

  const allSelected = scenarios.length > 0 && selectedIds.size === scenarios.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < scenarios.length;

  if (scenarios.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        {/* Empty state doesn't need selection header */}
        <svg
          className="w-12 h-12 text-gray-300 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
          />
        </svg>
        <p className="text-lg text-gray-500 mb-2">{t.noResults}</p>
        <p className="text-sm text-gray-400">
          {t.createFirstScenario}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selection header */}
      {selectable && (
        <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(input) => {
                if (input) {
                  input.indeterminate = someSelected;
                }
              }}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
            />
            <span className="text-sm text-gray-700">
              {allSelected
                ? (t.testSuites?.deselectAll || 'Deselect all')
                : (t.testSuites?.selectAll || 'Select all')}
            </span>
          </label>
          {selectedIds.size > 0 && (
            <span className="text-sm text-violet-600 font-medium">
              {selectedIds.size} {t.testSuites?.selected || 'selected'}
            </span>
          )}
        </div>
      )}

      {/* Scenario cards */}
      <div className="grid gap-4">
      {scenarios.map((scenario) => {
        const isExpanded = expandedIds.has(scenario.id);
        const enabledCriteria = scenario.criteria.filter(c => c.enabled !== false);

        return (
          <div
            key={scenario.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            {/* Main Card Content */}
            <div className="p-4">
              <div className="flex items-start justify-between">
                {/* Selection checkbox */}
                {selectable && (
                  <div className="flex-shrink-0 mr-3 pt-1">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(scenario.id)}
                      onChange={() => toggleSelection(scenario.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                    />
                  </div>
                )}

                {/* Left: Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {scenario.name}
                    </h3>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${getStatusClass(scenario)}`}
                    >
                      {getStatusText(scenario)}
                    </span>
                    {scenario.source === 'chat_promoted' && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {t.sourceChatPromoted}
                      </span>
                    )}
                    {!scenario.is_active && (
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                        {t.statusInactive}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {scenario.description || t.noDescription}
                  </p>

                  {/* Quick Info Row */}
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Bot className="w-3 h-3" />
                      <span className="font-medium">{scenario.agent_type}</span>
                    </span>
                    {scenario.category && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                        {scenario.category}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {t.minOverallScore}: {formatScore(scenario.min_overall_score)}
                    </span>
                    <span>
                      {t.llmJudgeModel}: <span className="font-medium">{scenario.judger_model_id}</span>
                    </span>
                  </div>

                  {/* Tags with inline editing */}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {(scenario.tags || []).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded group"
                      >
                        {tag}
                        {onTagsChange && (
                          <button
                            onClick={() => handleRemoveTag(scenario, tag)}
                            className="opacity-50 hover:opacity-100 transition-opacity"
                            title={t.removeTag || 'Remove tag'}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))}
                    {onTagsChange && (
                      <div className="inline-flex items-center">
                        <input
                          type="text"
                          value={newTagInputs[scenario.id] || ''}
                          onChange={(e) => setNewTagInputs(prev => ({ ...prev, [scenario.id]: e.target.value }))}
                          onKeyDown={(e) => handleAddTag(scenario, e)}
                          placeholder={t.addTagPlaceholder || '+ Add tag'}
                          className="w-20 px-2 py-0.5 text-xs border border-dashed border-gray-300 rounded focus:border-yellow-400 focus:outline-none"
                        />
                      </div>
                    )}
                  </div>

                  {/* Criteria Summary */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {enabledCriteria.slice(0, isExpanded ? undefined : 4).map((criterion) => (
                      <span
                        key={criterion.name}
                        className="inline-flex items-center px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 rounded"
                        title={`${criterion.name}: ${t.threshold} ${formatScore(criterion.threshold)}`}
                      >
                        {criterion.name}
                        <span className="ml-1 text-indigo-400">
                          {formatScore(criterion.threshold)}
                        </span>
                      </span>
                    ))}
                    {!isExpanded && enabledCriteria.length > 4 && (
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                        +{enabledCriteria.length - 4}
                      </span>
                    )}
                  </div>

                  {/* Conversation Preview */}
                  <div className="mt-3 text-sm text-gray-500">
                    <span className="font-medium">{t.input}: </span>
                    <span>
                      {(scenario.conversation?.[0]?.content || '').substring(0, isExpanded ? 500 : 100)}
                      {(scenario.conversation?.[0]?.content || '').length > (isExpanded ? 500 : 100) && '...'}
                    </span>
                  </div>
                </div>

                {/* Right: Score & Actions */}
                <div className="flex flex-col items-end gap-2 ml-4">
                  <div className="text-center">
                    <div
                      className={`text-2xl font-bold ${
                        scenario.latest_result?.passed
                          ? 'text-green-600'
                          : scenario.latest_result
                          ? 'text-red-600'
                          : 'text-gray-400'
                      }`}
                    >
                      {scenario.latest_result
                        ? formatScore(scenario.latest_result.score)
                        : '-'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {scenario.latest_result
                        ? formatDate(scenario.latest_result.executed_at)
                        : t.neverRun}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => onRun(scenario)}
                      disabled={runningScenarioIds.has(scenario.id)}
                      className={`px-3 py-1.5 text-sm rounded transition-colors ${
                        runningScenarioIds.has(scenario.id)
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-green-50 text-green-700 hover:bg-green-100'
                      }`}
                      title={runningScenarioIds.has(scenario.id)
                        ? (t.running || 'Running...')
                        : t.runSingle
                      }
                    >
                      {runningScenarioIds.has(scenario.id) ? (
                        <span className="animate-spin inline-block">🔄</span>
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </button>
                    {onViewHistory && (
                      <button
                        onClick={() => onViewHistory(scenario)}
                        className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded hover:bg-purple-100 transition-colors"
                        title={t.historyPanel?.title || 'History'}
                      >
                        <History className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(scenario)}
                      className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
                      title={t.edit}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {onDuplicate && (
                      <button
                        onClick={() => onDuplicate(scenario)}
                        className="px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded hover:bg-gray-100 transition-colors"
                        title={t.duplicate}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(scenario)}
                      className="px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors"
                      title={t.delete}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Expandable Details Section */}
            <div className="border-t border-gray-100">
              <button
                onClick={() => toggleExpanded(scenario.id)}
                className="w-full px-4 py-2 flex items-center justify-center gap-1 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    {t.collapseDetails || 'Collapse'}
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    {t.expandDetails || 'Show details'}
                  </>
                )}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-4">
                  {/* Expected Response */}
                  {scenario.expected_response && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">
                        {t.expectedResponse}
                      </h4>
                      <div className="p-3 bg-green-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
                        {scenario.expected_response}
                      </div>
                    </div>
                  )}

                  {/* Full Conversation */}
                  {scenario.conversation.length > 1 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">
                        {t.testConversation}
                      </h4>
                      <div className="space-y-2">
                        {scenario.conversation.map((turn, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg text-sm ${
                              turn.role === 'user'
                                ? 'bg-blue-50 text-blue-900'
                                : 'bg-gray-50 text-gray-700'
                            }`}
                          >
                            <span className="font-medium">
                              {turn.role === 'user' ? t.userRole : t.assistantRole}:
                            </span>{' '}
                            {turn.content}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Evaluation Criteria Details */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                      {t.evaluationCriteria}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {scenario.criteria.map((criterion) => (
                        <div
                          key={criterion.name}
                          className={`p-2 rounded border ${
                            criterion.enabled !== false
                              ? 'bg-white border-indigo-200'
                              : 'bg-gray-50 border-gray-200 opacity-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">{criterion.name}</span>
                            <span className="text-xs text-gray-500">
                              {t.threshold}: {formatScore(criterion.threshold)}
                            </span>
                          </div>
                          {criterion.weight !== 1 && (
                            <div className="text-xs text-gray-400 mt-1">
                              Weight: {criterion.weight}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Meta Info */}
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                    <span>ID: {scenario.id.slice(0, 8)}...</span>
                    <span>Priority: {scenario.priority}</span>
                    <span>Created: {formatDate(scenario.created_at)}</span>
                    <span>Updated: {formatDate(scenario.updated_at)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};
